import { services } from "@/core";

export default {
  name: "Dashboard",
  data() {
    return {
      activeTab: "principal",
      tabs: [
        { id: "principal", value: "Principal", active: true },
        { id: "uso", value: "Uso", active: false },
        { id: "churn", value: "Churn", active: false },
        { id: "financeiro", value: "Financeiro", active: false },
        { id: "conteudo", value: "Conteúdo", active: false },
      ],
      // Loading states
      loadingGeneral: false,
      loadingUsage: false,
      loadingChurn: false,
      loadingFinances: false,
      loadingContent: false,
      // General tab data
      generalData: null,
      monthlySignupsChart: { labels: [], values: [] },
      professionalTypeChart: { labels: [], datasets: [] },
      subscribersTypeChart: { labels: [], values: [] },
      gatewayChart: { labels: [], datasets: [] },
      cohortConversionChart: { labels: [], registrations: [], subscribers: [], conversionRate: [] },
      monthlyConversionFlowChart: { labels: [], registrations: [], newSubscribers: [], conversionRate: [] },
      // Usage tab data
      usageData: null,
      recipeTypeChart: { labels: [], values: [] },
      monthlyRecipesChart: { labels: [], values: [] },
      recipeDigitalTypeChart: { labels: [], values: [] },
      monthlyRecipesByDigitalTypeChart: { labels: [], datasets: [] },
      medicalRecordTypeChart: { labels: [], values: [] },
      monthlyMedicalRecordsChart: { labels: [], values: [] },
      doctorUsageChart: { labels: [], values: [] },
      doctorEngagementChart: { labels: [], values: [], colors: [] },
      featureAdoptionChart: { labels: [], values: [], total: 0 },
      contentViewsByUserTypeChart: { labels: [], values: [] },
      monthlyAiChatMessagesChart: { labels: [], values: [] },
      // Churn tab data
      churnData: null,
      stripeChurnChart: {
        labels: [],
        churnPercent: [],
        activeBase: [],
        newSubscriptions: [],
        cancellations: [],
        netGrowth: [],
      },
      inappChurnChart: {
        labels: [],
        churnPercent: [],
        activeBase: [],
        newSubscriptions: [],
        cancellations: [],
        netGrowth: [],
      },
      churnCohortChart: { labels: [], values: [] },
      inappChurnCohortChart: { labels: [], values: [] },
      // Finances tab data
      financesData: null,
      stripeMrrChart: { labels: [], values: [] },
      inappMrrChart: { labels: [], values: [] },
      totalMrrChart: { labels: [], values: [] },
      stripeRevenueChart: { labels: [], values: [] },
      inappRevenueChart: { labels: [], values: [] },
      totalRevenueChart: { labels: [], values: [] },
      // Content tab data
      contentData: null,
      topViewedContents: [],
      dailyContentViewsChart: { labels: [], values: [] },
      monthlyContentViewsChart: { labels: [], values: [] },
    };
  },
  mounted() {
    this.loadGeneralData();
  },
  methods: {
    handleTabClicked(id) {
      this.activeTab = id;
      this.tabs = this.tabs.map((tab) => ({
        ...tab,
        active: tab.id === id,
      }));

      // Load data for the selected tab if not already loaded
      if (id === "uso") {
        if (!this.usageData) this.loadUsageData();
        if (!this.contentData) this.loadContentData();
      }
      if (id === "churn" && !this.churnData) {
        this.loadChurnData();
      }
      if (id === "financeiro" && !this.financesData) {
        this.loadFinancesData();
      }
      if (id === "conteudo" && !this.contentData) {
        this.loadContentData();
      }
    },
    forceRefreshCurrentTab() {
      const loaders = {
        principal: () => this.loadGeneralData(true),
        conteudo: () => this.loadContentData(true),
        uso: () => this.loadUsageData(true),
        churn: () => this.loadChurnData(true),
        financeiro: () => this.loadFinancesData(true),
      };
      if (loaders[this.activeTab]) {
        loaders[this.activeTab]();
      }
    },
    async loadGeneralData(forceRefresh = false) {
      this.loadingGeneral = true;
      try {
        const params = forceRefresh ? { forceRefresh: true } : {};
        const res = await services.userService.dashboardGeneral(params);
        this.generalData = res.data;
        this.processChartData();
      } catch (error) {
        console.log("Erro ao carregar dashboard", error);
      } finally {
        this.loadingGeneral = false;
      }
    },
    async loadUsageData(forceRefresh = false) {
      this.loadingUsage = true;
      try {
        const params = forceRefresh ? { forceRefresh: true } : {};
        const res = await services.userService.dashboardUsage(params);
        this.usageData = res.data;
        this.processUsageChartData();
      } catch (error) {
        console.log("Erro ao carregar dados de uso", error);
      } finally {
        this.loadingUsage = false;
      }
    },
    async loadChurnData(forceRefresh = false) {
      this.loadingChurn = true;
      try {
        const params = forceRefresh ? { forceRefresh: true } : {};
        const res = await services.userService.dashboardChurn(params);
        this.churnData = res.data;
        this.processChurnChartData();
      } catch (error) {
        console.log("Erro ao carregar dados de churn", error);
      } finally {
        this.loadingChurn = false;
      }
    },
    async loadFinancesData(forceRefresh = false) {
      this.loadingFinances = true;
      try {
        const params = forceRefresh ? { forceRefresh: true } : {};
        const res = await services.userService.dashboardFinances(params);
        this.financesData = res.data;
        this.processFinancesChartData();
      } catch (error) {
        console.log("Erro ao carregar dados financeiros", error);
      } finally {
        this.loadingFinances = false;
      }
    },
    async loadContentData(forceRefresh = false) {
      this.loadingContent = true;
      try {
        const params = forceRefresh ? { forceRefresh: true } : {};
        const res = await services.userService.dashboardContent(params);
        this.contentData = res.data;
        this.processContentChartData();
      } catch (error) {
        console.log("Erro ao carregar dados de conteúdo", error);
      } finally {
        this.loadingContent = false;
      }
    },
    processChartData() {
      if (!this.generalData) return;

      // Monthly signups chart
      if (this.generalData.monthly_signups) {
        this.monthlySignupsChart = {
          labels: this.generalData.monthly_signups.map((item) => item.month),
          values: this.generalData.monthly_signups.map((item) => item.count),
        };
      }

      // Professional type chart (stacked bar)
      if (this.generalData.signups_by_professional_type) {
        const data = this.generalData.signups_by_professional_type;
        const colors = {
          doctor: "#2e4664",
          student: "#4a7c59",
          dentist: "#f4a261",
          other: "#e76f51",
        };
        const labels = {
          doctor: "Médicos",
          student: "Estudantes",
          dentist: "Dentistas",
          other: "Outros",
        };
        this.professionalTypeChart = {
          labels: data.map((item) => item.month),
          datasets: ["doctor", "student", "dentist", "other"].map((type) => ({
            label: labels[type],
            data: data.map((item) => item[type] || 0),
            backgroundColor: colors[type],
          })),
        };
      }

      // Subscribers by type chart (pie)
      if (this.generalData.subscribers_by_type) {
        this.subscribersTypeChart = {
          labels: this.generalData.subscribers_by_type.map((item) => item.type),
          values: this.generalData.subscribers_by_type.map(
            (item) => item.count,
          ),
        };
      }

      // Gateway chart (stacked bar)
      if (this.generalData.monthly_subscriptions_by_gateway) {
        const data = this.generalData.monthly_subscriptions_by_gateway;
        this.gatewayChart = {
          labels: data.map((item) => item.month),
          datasets: [
            {
              label: "Stripe",
              data: data.map((item) => item.stripe || 0),
              backgroundColor: "#2e4664",
            },
            {
              label: "In-App Purchase",
              data: data.map((item) => item.iap || 0),
              backgroundColor: "#4a7c59",
            },
          ],
        };
      }

      // Cohort conversion chart (bar + line)
      if (this.generalData.cohort_conversion) {
        const data = this.generalData.cohort_conversion;
        this.cohortConversionChart = {
          labels: data.map((item) => item.month),
          registrations: data.map((item) => item.registrations || 0),
          subscribers: data.map((item) => item.subscribers || 0),
          conversionRate: data.map((item) => item.conversion_rate || 0),
        };
      }

      // Monthly conversion flow chart (bar + line)
      if (this.generalData.monthly_conversion_flow) {
        const data = this.generalData.monthly_conversion_flow;
        this.monthlyConversionFlowChart = {
          labels: data.map((item) => item.month),
          registrations: data.map((item) => item.registrations || 0),
          newSubscribers: data.map((item) => item.new_subscribers || 0),
          conversionRate: data.map((item) => item.conversion_rate || 0),
        };
      }
    },
    processUsageChartData() {
      if (!this.usageData) return;

      // Recipe type chart (pie)
      if (this.usageData.recipe_count_by_type) {
        const typeLabels = {
          "exam request": "Pedido de Exame",
          simple: "Simples",
          "special control": "Controle Especial",
          "sick note": "Atestado",
          "free text": "Texto Livre",
        };
        const data = this.usageData.recipe_count_by_type;
        this.recipeTypeChart = {
          labels: Object.keys(data).map((key) => typeLabels[key] || key),
          values: Object.values(data),
        };
      }

      // Monthly recipes chart (bar)
      if (this.usageData.monthly_recipes) {
        this.monthlyRecipesChart = {
          labels: this.usageData.monthly_recipes.map((item) => item.month),
          values: this.usageData.monthly_recipes.map((item) => item.count),
        };
      }

      // Recipe by digital signature type chart (pie)
      if (this.usageData.recipe_count_by_digital_type) {
        const digitalTypeLabels = {
          sem_assinatura: "Sem Assinatura",
          vidaas: "Vidaas",
          birdId: "BirdID",
        };
        const data = this.usageData.recipe_count_by_digital_type;
        this.recipeDigitalTypeChart = {
          labels: Object.keys(data).map((key) => digitalTypeLabels[key] || key),
          values: Object.values(data),
        };
      }

      // Monthly recipes by digital type chart (stacked bar)
      if (this.usageData.monthly_recipes_by_digital_type) {
        const data = this.usageData.monthly_recipes_by_digital_type;
        const colors = {
          sem_assinatura: "#9ca3af",
          vidaas: "#2e4664",
          birdId: "#4a7c59",
        };
        const labels = {
          sem_assinatura: "Sem Assinatura",
          vidaas: "Vidaas",
          birdId: "BirdID",
        };
        this.monthlyRecipesByDigitalTypeChart = {
          labels: data.map((item) => item.month),
          datasets: ["sem_assinatura", "vidaas", "birdId"].map((type) => ({
            label: labels[type],
            data: data.map((item) => parseInt(item[type]) || 0),
            backgroundColor: colors[type],
          })),
        };
      }

      // Medical record by type chart (pie)
      if (this.usageData.medical_record_count_by_type) {
        const recordTypeLabels = {
          traditional_clinic: "Clínico Tradicional",
          sem_tipo: "Sem Tipo",
          soap: "SOAP",
        };
        const data = this.usageData.medical_record_count_by_type;
        this.medicalRecordTypeChart = {
          labels: Object.keys(data).map((key) => recordTypeLabels[key] || key),
          values: Object.values(data),
        };
      }

      // Monthly medical records chart (bar)
      if (this.usageData.monthly_medical_records) {
        this.monthlyMedicalRecordsChart = {
          labels: this.usageData.monthly_medical_records.map(
            (item) => item.month,
          ),
          values: this.usageData.monthly_medical_records.map(
            (item) => item.count,
          ),
        };
      }

      // Doctor usage charts (new structure with 3 charts)
      if (this.usageData.doctor_subscriber_usage_type) {
        const data = this.usageData.doctor_subscriber_usage_type;

        // Chart 1: Engajamento por quantidade de features (pie)
        if (data.por_quantidade) {
          const engagementLabels = {
            usa_0_features: "Nenhuma",
            usa_1_feature: "1 feature",
            usa_2_features: "2 features",
            usa_3_features: "3 features",
            usa_4_features: "Todas (4)",
          };
          const engagementColors = {
            usa_0_features: "#9ca3af",
            usa_1_feature: "#f59e0b",
            usa_2_features: "#f97316",
            usa_3_features: "#2e4664",
            usa_4_features: "#4a7c59",
          };
          this.doctorEngagementChart = {
            labels: Object.keys(engagementLabels).map((key) => engagementLabels[key]),
            values: Object.keys(engagementLabels).map((key) => data.por_quantidade[key] || 0),
            colors: Object.keys(engagementColors).map((key) => engagementColors[key]),
          };
        }

        // Chart 2: Adoção por feature (horizontal bar)
        if (data.por_feature) {
          const featureLabels = {
            receitas: "Receitas",
            prontuarios: "Prontuários",
            conteudos: "Conteúdos",
            ai_chat: "Chat IA",
          };
          this.featureAdoptionChart = {
            labels: Object.keys(featureLabels).map((key) => featureLabels[key]),
            values: Object.keys(featureLabels).map((key) => data.por_feature[key] || 0),
            total: data.total_medicos_pagantes || 0,
          };
        }

        // Chart 3: Detalhado (pie) - mantém compatibilidade
        if (data.detalhado) {
          const usageLabels = {
            somente_receitas: "Somente Receitas",
            somente_prontuarios: "Somente Prontuários",
            somente_conteudos: "Somente Conteúdos",
            somente_ai_chat: "Somente Chat IA",
            receitas_e_prontuarios: "Receitas e Prontuários",
            receitas_e_conteudos: "Receitas e Conteúdos",
            receitas_e_ai_chat: "Receitas e Chat IA",
            prontuarios_e_conteudos: "Prontuários e Conteúdos",
            prontuarios_e_ai_chat: "Prontuários e Chat IA",
            conteudos_e_ai_chat: "Conteúdos e Chat IA",
            receitas_prontuarios_conteudos: "Receitas, Prontuários e Conteúdos",
            receitas_prontuarios_ai_chat: "Receitas, Prontuários e Chat IA",
            receitas_conteudos_ai_chat: "Receitas, Conteúdos e Chat IA",
            prontuarios_conteudos_ai_chat: "Prontuários, Conteúdos e Chat IA",
            todas_atividades: "Todas Atividades",
            sem_atividade: "Sem Atividade",
          };
          this.doctorUsageChart = {
            labels: Object.keys(usageLabels).map((key) => usageLabels[key]),
            values: Object.keys(usageLabels).map((key) => data.detalhado[key] || 0),
          };
        }
      }

      // Monthly AI chat messages chart (bar)
      if (this.usageData.monthly_ai_chat_messages) {
        this.monthlyAiChatMessagesChart = {
          labels: this.usageData.monthly_ai_chat_messages.map((item) => item.month),
          values: this.usageData.monthly_ai_chat_messages.map((item) => item.count),
        };
      }
    },
    buildChurnChartData(items) {
      return {
        labels: items.map((item) => item.year_month),
        churnPercent: items.map((item) => item.churn_percent || 0),
        activeBase: items.map((item) => item.active_base || 0),
        newSubscriptions: items.map((item) => item.new_subscriptions || 0),
        cancellations: items.map((item) => item.cancellations || 0),
        netGrowth: items.map((item) => item.net_growth || 0),
      };
    },
    processChurnChartData() {
      if (!this.churnData) return;

      // Stripe churn chart
      if (this.churnData.stripe_churn) {
        this.stripeChurnChart = this.buildChurnChartData(
          this.churnData.stripe_churn,
        );
      }

      // In-App churn chart
      if (this.churnData.inapp_churn) {
        this.inappChurnChart = this.buildChurnChartData(
          this.churnData.inapp_churn,
        );
      }

      // Churn cohort chart (bar) - tempo até cancelar
      if (this.churnData.stripe_churn_cohort) {
        const data = this.churnData.stripe_churn_cohort;
        this.churnCohortChart = {
          labels: data.map(
            (item) =>
              `${item.months_active} ${
                item.months_active === 1 ? "mês" : "meses"
              }`,
          ),
          values: data.map((item) => item.count),
        };
      }

      // In-App churn cohort chart (bar)
      if (this.churnData.inapp_churn_cohort) {
        const data = this.churnData.inapp_churn_cohort;
        this.inappChurnCohortChart = {
          labels: data.map(
            (item) =>
              `${item.months_active} ${
                item.months_active === 1 ? "mês" : "meses"
              }`,
          ),
          values: data.map((item) => item.count),
        };
      }
    },
    processFinancesChartData() {
      if (!this.financesData) return;

      if (this.financesData.stripe_mrr) {
        this.stripeMrrChart = {
          labels: this.financesData.stripe_mrr.map((item) => item.year_month),
          values: this.financesData.stripe_mrr.map((item) => item.mrr),
        };
      }

      if (this.financesData.inapp_mrr) {
        this.inappMrrChart = {
          labels: this.financesData.inapp_mrr.map((item) => item.year_month),
          values: this.financesData.inapp_mrr.map((item) => item.mrr),
        };
      }

      if (this.financesData.total_mrr) {
        this.totalMrrChart = {
          labels: this.financesData.total_mrr.map((item) => item.year_month),
          values: this.financesData.total_mrr.map((item) => item.mrr),
        };
      }

      if (this.financesData.stripe_revenue) {
        this.stripeRevenueChart = {
          labels: this.financesData.stripe_revenue.map(
            (item) => item.year_month,
          ),
          values: this.financesData.stripe_revenue.map((item) => item.revenue),
        };
      }

      if (this.financesData.inapp_revenue) {
        this.inappRevenueChart = {
          labels: this.financesData.inapp_revenue.map(
            (item) => item.year_month,
          ),
          values: this.financesData.inapp_revenue.map((item) => item.revenue),
        };
      }

      if (this.financesData.total_revenue) {
        this.totalRevenueChart = {
          labels: this.financesData.total_revenue.map(
            (item) => item.year_month,
          ),
          values: this.financesData.total_revenue.map((item) => item.revenue),
        };
      }
    },
    processContentChartData() {
      if (!this.contentData) return;

      // Top viewed contents list
      if (this.contentData.top_viewed_contents) {
        this.topViewedContents = this.contentData.top_viewed_contents;
      }

      // Daily content views chart (bar)
      if (this.contentData.daily_content_views) {
        this.dailyContentViewsChart = {
          labels: this.contentData.daily_content_views.map((item) => item.date),
          values: this.contentData.daily_content_views.map(
            (item) => item.count,
          ),
        };
      }

      // Monthly content views chart (bar)
      if (this.contentData.monthly_content_views) {
        this.monthlyContentViewsChart = {
          labels: this.contentData.monthly_content_views.map(
            (item) => item.month,
          ),
          values: this.contentData.monthly_content_views.map(
            (item) => item.count,
          ),
        };
      }

      // Content views by user type chart (pie) - usado na aba Uso
      if (this.contentData.content_views_by_user_type) {
        const userTypeLabels = {
          gratuitos_com_conteudo: "Gratuitos com conteúdo",
          gratuitos_sem_conteudo: "Gratuitos sem conteúdo",
          assinantes_com_conteudo: "Assinantes com conteúdo",
          assinantes_sem_conteudo: "Assinantes sem conteúdo",
        };
        const data = this.contentData.content_views_by_user_type;
        this.contentViewsByUserTypeChart = {
          labels: Object.keys(data).map((key) => userTypeLabels[key] || key),
          values: Object.values(data),
        };
      }
    },
  },
};
